import { getHomeboxServiceUrl } from "../utils/urlConfig";
import { useTheme } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';
import { useState, useContext, useEffect, useRef } from 'react';
import { fetchAllAssets } from '../services/assetService';
import { fetchAllLocations } from '../services/locationService';
import '../style/shared/Topbar.css';

const Topbar = ({ toggleMenu, className, menuItems, handleMenuClick }) => {
  const togleClass = `toggle ${className}`;
  const { theme, setTheme } = useTheme();
  const { profileImage } = useContext(AuthContext);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const API_BASE_URL = getHomeboxServiceUrl();

  useEffect(() => {
    // Fetch all assets and locations when component mounts
    const loadData = async () => {
      try {
        const [assets, locations] = await Promise.all([
          fetchAllAssets(),
          fetchAllLocations()
        ]);
        setAllAssets(assets || []);
        setAllLocations(locations || []);
      } catch (error) {
        console.error("Failed to fetch data for search", error);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim() === '') {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const lowerQuery = query.toLowerCase();

    // Filter Menu Items
    const filteredMenu = menuItems ? menuItems.filter(item =>
      item.title.toLowerCase().includes(lowerQuery)
    ).map(item => ({ ...item, type: 'menu' })) : [];

    // Filter Assets
    const filteredAssets = allAssets.filter(asset =>
      asset.name?.toLowerCase().includes(lowerQuery)
    ).map(asset => ({ ...asset, type: 'asset' }));

    // Filter Locations
    const filteredLocations = allLocations.filter(location =>
      location.name?.toLowerCase().includes(lowerQuery)
    ).map(location => ({ ...location, type: 'location' }));

    setSearchResults([...filteredMenu, ...filteredAssets, ...filteredLocations]);
    setShowResults(true);
  };

  const handleResultClick = (result) => {
    setSearchQuery('');
    setShowResults(false);

    if (result.type === 'menu') {
      handleMenuClick(result.key);
    } else if (result.type === 'asset') {
      // Open public asset page in new tab
      window.open(`/public/assets/${result.id}`, '_blank');
    } else if (result.type === 'location') {
      // Open public location page in new tab
      window.open(`/public/locations/${result.id}`, '_blank');
    }
  };

  const getProfileImageUrl = (path) => {
    if (!path) return '/images/user.png';
    if (path.startsWith('http')) return path;
    const baseUrl = API_BASE_URL.replace('/api', '');
    return `${baseUrl}${path}`;
  };

  const themes = [
    { id: 'blue', name: 'Blue', color: '#4F46E5' },
    { id: 'green', name: 'Green', color: '#10B981' },
    { id: 'purple', name: 'Purple', color: '#8B5CF6' },
    { id: 'white', name: 'White', color: '#FFFFFF', border: '1px solid #ddd' },
    { id: 'dark', name: 'Dark', color: '#1E293B' },
    { id: 'god-tier', name: 'God-Tier', color: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)' },
    { id: 'golden', name: 'Golden', color: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)' }
  ];

  return (
    <div className="topbar">
      <div className={togleClass} onClick={toggleMenu}>
        <i className={className === "active" ? "fa-solid fa-chevron-right" : "fa-solid fa-bars-staggered"}></i>
      </div>

      <div className="search" ref={searchRef}>
        <label>
          <input
            type="text"
            placeholder="Search Menu & Assets"
            value={searchQuery}
            onChange={handleSearch}
            onFocus={() => { if (searchQuery) setShowResults(true); }}
          />
          <i className="fa fa-search" aria-hidden="true"></i>
        </label>

        {showResults && (
          <div className="search-results">
            {searchResults.length > 0 ? (
              <ul>
                {searchResults.map((result, index) => (
                  <li key={`${result.type}-${result.id || result.key || index}`} onClick={() => handleResultClick(result)}>
                    <i className={`icon ${result.type === 'menu' ? result.icon :
                      result.type === 'asset' ? 'fa-solid fa-box' :
                        'fa-solid fa-map-location-dot'
                      }`}></i>
                    <div className="info">
                      <span className="name">{result.type === 'menu' ? result.title : result.name}</span>
                      <span className="type">
                        {result.type === 'menu' ? 'Menu' :
                          result.type === 'asset' ? 'Asset' : 'Container'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="no-results">No results found</div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        {/* Theme Switcher */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <div
            className="theme-toggle"
            onClick={() => setShowThemeMenu(!showThemeMenu)}
            title="Change Theme"
          >
            <i className="fa-solid fa-palette"></i>
          </div>

          {showThemeMenu && (
            <div className="theme-menu">
              {themes.map(t => (
                <div
                  key={t.id}
                  onClick={() => { setTheme(t.id); setShowThemeMenu(false); }}
                  className={`theme-option ${theme === t.id ? 'active' : ''}`}
                >
                  <div className="theme-dot" style={{ background: t.color }}></div>
                  {t.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="user">
          <img src={getProfileImageUrl(profileImage)} alt="User" />
        </div>
      </div>
    </div>
  );
};

export default Topbar;
